import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { z } from 'zod/v4';

import {
  type ApiResponse,
  addActionNameToMutationResult,
  handleZodError,
  useLegacyAxiosInstance,
} from './utils';

const UpdateAccountManagerInputSchema = z.object({
  accountManagerId: z.number().nullable(),
});

const UpdateAccountManagerSchema = UpdateAccountManagerInputSchema.transform(data => ({
  account_manager_id: data.accountManagerId,
}));

export type UpdateAccountManagerVariables = z.infer<typeof UpdateAccountManagerInputSchema>;

export async function updateAccountManagerFn(
  axiosInstance: Promise<AxiosInstance>,
  { id, data }: { id: string; data: UpdateAccountManagerVariables },
) {
  const instance = await axiosInstance;
  const snakeCaseData = UpdateAccountManagerSchema.parse(data);
  const safeParse = UpdateAccountManagerSchema.safeParse(data);

  if (!safeParse.success) {
    handleZodError(safeParse, 'account manager');
  }

  return instance.post(`/debtors/${id}/assign_debtor`, snakeCaseData);
}

export function useUpdateAccountManager() {
  const { t } = useTranslation();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { id: string; data: UpdateAccountManagerVariables }
  >({
    mutationFn: variables => updateAccountManagerFn(axiosInstance, variables),
    onSuccess: data => {
      notifications.show({
        message: data.data.message,
        color: 'green',
      });
    },
    onError: error => {
      error.response?.data.error_message.forEach((message: string) => {
        notifications.show({
          title: t(i18nKeys.ERROR.ERROR),
          color: 'red',
          message,
        });
      });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { id: string; data: UpdateAccountManagerVariables },
    'updateAccountManager'
  >('updateAccountManager', mutation);
}
